import { useCallback, useState } from 'react';
import {
  normalizeGroupValue,
  serializeGroupValue,
} from '../utils/group';

const defaultSelection = {
  eventId: null,
  competitionId: null,
  competitionName: '',
  classificationId: null,
  stageId: null,
  stageStatus: null,
  stageStatusDesc: '',
  stageIsTested: 0,
  timingPhase: 'NOT_STARTED',
  group: [],
};

const readNumber = (key) => {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = window.localStorage.getItem(key);
  if (value === null || value === undefined || value === 'null') {
    return null;
  }

  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const readGroupValue = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  return normalizeGroupValue(window.localStorage.getItem('GroupID'));
};

const shallowEqualSelection = (prev, next) => {
  const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);

  for (const key of keys) {
    const prevValue = prev[key];
    const nextValue = next[key];

    const bothArrays = Array.isArray(prevValue) && Array.isArray(nextValue);
    if (bothArrays) {
      if (serializeGroupValue(prevValue) !== serializeGroupValue(nextValue)) {
        return false;
      }
      continue;
    }

    if (prevValue !== nextValue) {
      return false;
    }
  }

  return true;
};

const usePersistedSelection = (eventId) => {
  const [selection, setSelection] = useState(() => ({
    ...defaultSelection,
    eventId,
    competitionId: readNumber('CompetitionID'),
    classificationId: readNumber('ClassificationID'),
    stageId: readNumber('StageID'),
    competitionName:
      typeof window === 'undefined'
        ? ''
        : window.localStorage.getItem('CompetitionName') || '',
    group: readGroupValue(),
  }));

  const persistSelection = useCallback(
    (nextSelection) => {
      if (typeof window === 'undefined') {
        return;
      }

      const { localStorage } = window;

      if (nextSelection.competitionId === null) {
        localStorage.removeItem('CompetitionID');
      } else {
        localStorage.setItem('CompetitionID', nextSelection.competitionId);
      }

      if (nextSelection.classificationId === null) {
        localStorage.removeItem('ClassificationID');
      } else {
        localStorage.setItem('ClassificationID', nextSelection.classificationId);
      }

      if (nextSelection.stageId === null) {
        localStorage.removeItem('StageID');
      } else {
        localStorage.setItem('StageID', nextSelection.stageId);
      }

      localStorage.setItem('GroupID', serializeGroupValue(nextSelection.group));
      localStorage.setItem('EventID', eventId);
      localStorage.setItem('CompetitionName', nextSelection.competitionName || '');
    },
    [eventId]
  );

  const updateSelection = useCallback(
    (updates) => {
      setSelection((prev) => {
        const next = { ...prev, ...updates };
        if (shallowEqualSelection(prev, next)) {
          return prev;
        }

        persistSelection(next);
        return next;
      });
    },
    [persistSelection]
  );

  const setGroup = useCallback(
    (groupValue) => {
      const normalized = normalizeGroupValue(groupValue);
      updateSelection({ group: normalized });
    },
    [updateSelection]
  );

  return {
    selection,
    updateSelection,
    setGroup,
  };
};

export default usePersistedSelection;
